import { alpha, Container, Typography, Button, Box, CircularProgress, Dialog, TextField } from '@mui/material'
import React, { useEffect, useState } from 'react'
import styles from './trackingStatusStyles'
import { grey, orange, red, yellow } from '@mui/material/colors';
import { cancelGuide, setGuideIncidence } from '../../../api/guidesApi';
import { notification } from '../../../utils/Notifications';
import useLogout from '../../../hooks/auth/authHook';
import { usePermissions } from '../../../hooks/permissions/usePermissions';
import { Cancel, ReportProblem } from '@mui/icons-material';

// id_status (tabla package_status) en los que la guía NO puede pasar a "Incidencia":
// 1 = Guía Creada, 9 = Entregado, 11 = Devuelto, 14 = Cancelado,
// 17 = Paquete con Incidencia (ya está en ese status, no tiene caso volver a marcarla).
const NON_INCIDENCE_STATUSES = [1, 9, 11, 14, 17];

const TrackingStatus = ({guide, onGuideUpdated, trackingCode}) => {
  const [logout] = useLogout();
  const { hasPermission } = usePermissions();
  const [isCanceling, setIsCanceling] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [isSettingIncidence, setIsSettingIncidence] = useState(false);
  const [openIncidenceModal, setOpenIncidenceModal] = useState(false);
  const [incidenceReason, setIncidenceReason] = useState('');

  const getStatusColor = () => {
    if (guide?.guide_info?.status_keyname === 'Entregado') {
      return "#53CA99";
    } else if (guide?.guide_info?.status_keyname === 'Cancelado') {
      return red[500];
    } else if (guide?.guide_info?.status_keyname === 'Devuelto') {
      return yellow[700];
    } else if (guide?.guide_info?.status_keyname === 'Paquete con Incidencia') {
      return orange[700];
    } else {
      return grey[700];
    }
  };

  const isInMatriz = guide?.guide_info?.current_status?.id_status === 6 || guide?.guide_info?.current_status?.id_status === 1;

  const currentIdStatus = guide?.guide_info?.current_status?.id_status;
  const canSetIncidence = currentIdStatus !== undefined && currentIdStatus !== null && !NON_INCIDENCE_STATUSES.includes(currentIdStatus);

  const handleCancelGuide = () => {
    setOpenCancelModal(true);
  };

  const handleCloseCancelModal = () => {
    setOpenCancelModal(false);
    setCancellationReason('');
  };

  const handleConfirmCancel = () => {
    //const trackingCode = guide?.guide_info?.tracking_code;
    
    if (isCanceling || !trackingCode || !cancellationReason.trim()) {
      if (!cancellationReason.trim()) {
        notification({
          text: "Por favor, ingresa una razón de cancelación",
          type: "error",
        });
      }
      return;
    }

    setIsCanceling(true);

    cancelGuide(trackingCode, cancellationReason.trim())
      .then((response) => {
        if (response.data.status_Code === 200) {
          notification({
            text: "Guía cancelada exitosamente",
            type: "success",
          });
          handleCloseCancelModal();
          if (onGuideUpdated) {
            onGuideUpdated();
          }
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          handleCloseCancelModal();
          logout();
        } else {
          notification({
            text: "Error al cancelar la guía",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      })
      .finally(() => {
        setIsCanceling(false);
      });
  };

  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    handleCloseCancelModal();
  };

  const handleSetIncidence = () => {
    setOpenIncidenceModal(true);
  };

  const handleCloseIncidenceModal = () => {
    setOpenIncidenceModal(false);
    setIncidenceReason('');
  };

  const handleConfirmIncidence = () => {
    if (isSettingIncidence || !trackingCode || !incidenceReason.trim()) {
      if (!incidenceReason.trim()) {
        notification({
          text: "Por favor, ingresa una razón de la incidencia",
          type: "error",
        });
      }
      return;
    }

    setIsSettingIncidence(true);

    setGuideIncidence(trackingCode, incidenceReason.trim())
      .then((response) => {
        if (response.data.status_Code === 200) {
          notification({
            text: "Guía marcada con incidencia exitosamente",
            type: "success",
          });
          handleCloseIncidenceModal();
          if (onGuideUpdated) {
            onGuideUpdated();
          }
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          handleCloseIncidenceModal();
          logout();
        } else {
          notification({
            text: "Error al marcar la guía con incidencia",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      })
      .finally(() => {
        setIsSettingIncidence(false);
      });
  };

  const handleIncidenceDialogClose = (event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    handleCloseIncidenceModal();
  };

  useEffect(() => {

  }, [guide]);

  return (
    <>
      <Container style={styles.mainContainer}>
        <Typography variant="h6" sx={{fontWeight: 'bold'}}>Estado de la guía</Typography>
        <Container sx={{color: getStatusColor(), backgroundColor: alpha(getStatusColor(), 0.15), borderRadius: '8px', padding: '10px 0px' }}>
          <Typography variant="h6" sx={{fontWeight: 'bold'}}>{guide?.guide_info?.status_keyname ?? 'Desconocido'}</Typography>
          {/* <Typography variant="body2">{guide?.guide_info?.status_type}</Typography> */}
        </Container>
        {((isInMatriz && hasPermission('trk_eg')) || (canSetIncidence && hasPermission('trk_inc'))) && (
          <Box sx={styles.buttonsContainer}>
            {isInMatriz && hasPermission('trk_eg') && (
              <Button
                variant="text"
                startIcon={isCanceling ? <CircularProgress size={16} sx={{ color: red['A400'], width: '16px', height: '16px' }} /> : <Cancel />}
                onClick={handleCancelGuide}
                disabled={isCanceling}
                sx={styles.activeButton}
              >
                Cancelar guía
              </Button>
            )}
            {canSetIncidence && hasPermission('trk_inc') && (
              <Button
                variant="text"
                startIcon={isSettingIncidence ? <CircularProgress size={16} sx={{ color: orange[700], width: '16px', height: '16px' }} /> : <ReportProblem />}
                onClick={handleSetIncidence}
                disabled={isSettingIncidence}
                sx={styles.incidenceButton}
              >
                Marcar incidencia
              </Button>
            )}
          </Box>
        )}
      </Container>

      <Dialog
        onClose={handleDialogClose}
        open={openCancelModal}
        PaperProps={{ style: { borderRadius: "16px" } }}
      >
        <Container style={{ padding: 26, minWidth: 400 }}>
          <Typography
            component="div"
            variant="h5"
            style={{
              textAlign: "center",
              fontWeight: "bold",
              color: grey[800],
              marginBottom: 26,
            }}
          >
            Cancelar guía
          </Typography>
          <Typography
            variant="body1"
            style={{
              marginBottom: 20,
              color: grey[600],
            }}
          >
            Ingresa la razón de cancelación de la guía
          </Typography>
          <TextField
            id="cancellation-reason"
            label="Razón de cancelación"
            variant="outlined"
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            multiline
            rows={4}
            fullWidth
            required
            style={{ marginBottom: 24 }}
            inputProps={{ maxLength: 100 }}
            helperText={`${cancellationReason.length}/100 caracteres`}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: red['A400'],
                },
                '&:hover fieldset': {
                  borderColor: red['A400'],
                },
                '&.Mui-focused fieldset': {
                  borderColor: red['A400'],
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: red['A400'],
              },
            }}
          />
          <Container
            style={{
              display: "flex",
              justifyContent: "end",
              width: "100%",
              padding: 0,
              gap: 12,
            }}
          >
            <Button
              variant="standard"
              color="primary"
              onClick={handleDialogClose}
              disabled={isCanceling}
              style={{
                width: "120px",
                borderRadius: "0.5rem",
                height: "38px",
                color: red["A400"],
                backgroundColor: red["50"],
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={!cancellationReason.trim() || isCanceling}
              onClick={() => handleConfirmCancel()}
              sx={{
                width: "120px",
                borderRadius: "0.5rem",
                height: "38px",
                backgroundColor: cancellationReason.trim() && !isCanceling ? red['A400'] : grey[300],
                color: cancellationReason.trim() && !isCanceling ? 'white' : grey[500],
                '&:hover': {
                  backgroundColor: cancellationReason.trim() && !isCanceling ? red['A700'] : grey[300],
                },
              }}
            >
              {isCanceling ? (
                <CircularProgress size={24} style={{ color: "white" }} />
              ) : (
                "Confirmar"
              )}
            </Button>
          </Container>
        </Container>
      </Dialog>

      <Dialog
        onClose={handleIncidenceDialogClose}
        open={openIncidenceModal}
        PaperProps={{ style: { borderRadius: "16px" } }}
      >
        <Container style={{ padding: 26, minWidth: 400 }}>
          <Typography
            component="div"
            variant="h5"
            style={{
              textAlign: "center",
              fontWeight: "bold",
              color: grey[800],
              marginBottom: 26,
            }}
          >
            Marcar guía con incidencia
          </Typography>
          <Typography
            variant="body1"
            style={{
              marginBottom: 20,
              color: grey[600],
            }}
          >
            Esta acción notificará al usuario que generó la guía. Ingresa la razón de la incidencia para confirmar.
          </Typography>
          <TextField
            id="incidence-reason"
            label="Razón de la incidencia"
            variant="outlined"
            value={incidenceReason}
            onChange={(e) => setIncidenceReason(e.target.value)}
            multiline
            rows={4}
            fullWidth
            required
            style={{ marginBottom: 24 }}
            inputProps={{ maxLength: 100 }}
            helperText={`${incidenceReason.length}/100 caracteres`}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: orange[700],
                },
                '&:hover fieldset': {
                  borderColor: orange[700],
                },
                '&.Mui-focused fieldset': {
                  borderColor: orange[700],
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: orange[700],
              },
            }}
          />
          <Container
            style={{
              display: "flex",
              justifyContent: "end",
              width: "100%",
              padding: 0,
              gap: 12,
            }}
          >
            <Button
              variant="standard"
              color="primary"
              onClick={handleIncidenceDialogClose}
              disabled={isSettingIncidence}
              style={{
                width: "120px",
                borderRadius: "0.5rem",
                height: "38px",
                color: orange[700],
                backgroundColor: orange[50],
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={!incidenceReason.trim() || isSettingIncidence}
              onClick={() => handleConfirmIncidence()}
              sx={{
                width: "120px",
                borderRadius: "0.5rem",
                height: "38px",
                backgroundColor: incidenceReason.trim() && !isSettingIncidence ? orange[700] : grey[300],
                color: incidenceReason.trim() && !isSettingIncidence ? 'white' : grey[500],
                '&:hover': {
                  backgroundColor: incidenceReason.trim() && !isSettingIncidence ? orange[900] : grey[300],
                },
              }}
            >
              {isSettingIncidence ? (
                <CircularProgress size={24} style={{ color: "white" }} />
              ) : (
                "Confirmar"
              )}
            </Button>
          </Container>
        </Container>
      </Dialog>
    </>
  )
}

export default TrackingStatus